import React from "react";
import { Box, Divider, Grid, Typography, Button, Paper } from "@mui/material";
import useIsMobile from "../../shared_modules/useIsMobile";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import { useTenant } from "../../hooks/useTenant";
import { formatPhoneNumber } from "../../utils/functions";

const SuccessfullBooking = ({ bookingResult, onBookAgain }) => {
  const isMobile = useIsMobile();
  const { tenant } = useTenant();

  const booking = bookingResult?.booking;
  const payment = bookingResult?.payment;
  const policy = bookingResult?.policy;

  const startsAt = booking?.starts_at
    ? new Date(booking.starts_at.replace(" ", "T") + "Z")
    : null;

  const cancelWindowHours = tenant?.cancellation_window_hours ?? 24;
  const cancelDeadline = startsAt
    ? new Date(startsAt.getTime() - cancelWindowHours * 60 * 60 * 1000)
    : null;

  const cancelDeadlineFormatted = cancelDeadline
    ? cancelDeadline.toLocaleString("en-US", {
        timeZone: tenant?.timezone || "America/New_York",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : policy?.cancel_by;

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        px: isMobile ? 3 : 4,
        py: 6,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <CheckCircleOutlineOutlinedIcon
          sx={{ color: "success.main", fontSize: "1.4rem" }}
        />
        <Typography
          variant="overline"
          sx={{ color: "success.main", fontWeight: 600 }}
        >
          Booking Confirmed
        </Typography>
      </Box>

      <Typography variant="h2" sx={{ mb: 1, textAlign: "center" }}>
        You're all set.
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", textAlign: "center" }}
      >
        A confirmation has been sent to your email.
      </Typography>

      {/* Magic link callout */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1.5,
          mb: 4,
          px: 2.5,
          py: 1.5,
          borderRadius: "10px",
          backgroundColor: "rgba(196,152,77,0.08)",
          border: "1px solid rgba(196,152,77,0.2)",
          maxWidth: "620px",
          width: "100%",
        }}
      >
        <MailOutlineOutlinedIcon
          sx={{ color: "#C4984D", fontSize: "1.1rem", flexShrink: 0 }}
        />
        <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#111" }}>
          Your email includes a <strong>magic link</strong> to cancel or
          reschedule your appointment — no account needed.
        </Typography>
      </Box>

      {/* Booking summary */}
      <Box
        component={Paper}
        elevation={1}
        sx={{
          width: "100%",
          maxWidth: "620px",
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          p: 3,
        }}
      >
        {/* Barber row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: "4rem",
                height: "4rem",
                borderRadius: "50%",
                backgroundColor: "background.default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                fontWeight: 700,
                color: "text.third",
                border: "1px solid",
                borderColor: "customDivider",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {booking?.barber?.avatar_url ? (
                <Box
                  component="img"
                  src={booking.barber.avatar_url}
                  alt={`${booking.barber.first_name} ${booking.barber.last_name}`}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <>
                  {booking?.barber?.first_name?.[0]}
                  {booking?.barber?.last_name?.[0]}
                </>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {booking?.barber?.first_name} {booking?.barber?.last_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "success.main" + "20",
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "success.main", fontWeight: 600 }}
            >
              BOOKED
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "customDivider", mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: "Service", value: booking?.service?.name },
            {
              label: "Duration",
              value: booking?.service?.duration
                ? `${booking.service.duration} min`
                : "—",
            },
            {
              label: "Date",
              value: startsAt
                ? startsAt.toLocaleDateString("en-US", {
                    timeZone: tenant?.timezone || "America/New_York",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—",
            },
            {
              label: "Time",
              value: startsAt
                ? startsAt.toLocaleTimeString("en-US", {
                    timeZone: tenant?.timezone || "America/New_York",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "—",
            },
          ].map(({ label, value }) => (
            <Grid key={label} size={isMobile ? 6 : 3}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.5 }}
              >
                {label}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {value || "—"}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: "customDivider", mb: 2 }} />

        {/* Payment */}
        {payment && (
          <>
            {booking?.payment_method === "card" ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.8,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Service price
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${payment.service_price}
                  </Typography>
                </Box>
                {Number(payment.tip_amount) > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.8,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Tip for barber
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#C4984D" }}
                    >
                      +${payment.tip_amount}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.8,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Processing fee
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${payment.processing_fee}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.8,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Amount due at appointment
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  ${payment.service_price} cash
                </Typography>
              </Box>
            )}

            <Divider sx={{ borderColor: "customDivider", my: 1.5 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {booking?.payment_method === "card"
                  ? "Total charged today"
                  : "Due at appointment"}
              </Typography>
              <Typography variant="h4" sx={{ color: "text.third" }}>
                {booking?.payment_method === "card"
                  ? `$${payment.total_charged}`
                  : `$${payment.service_price} cash`}
              </Typography>
            </Box>
          </>
        )}

        {/* Policy reminder */}
        {policy && (
          <>
            <Divider sx={{ borderColor: "customDivider", mt: 2, mb: 2 }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.78rem",
                lineHeight: 1.6,
              }}
            >
              Free cancellation until{" "}
              <strong style={{ color: "#111008" }}>
                {cancelDeadlineFormatted}
              </strong>
              .{" "}
              {booking?.payment_method === "card" ? (
                <>
                  If cancelled in time,{" "}
                  <strong style={{ color: "#111008" }}>
                    $
                    {(
                      Number(payment?.service_price) +
                      Number(payment?.tip_amount || 0)
                    ).toFixed(2)}
                  </strong>
                  {Number(payment?.tip_amount) > 0 ? " (service + tip)" : ""}{" "}
                  will be refunded — the processing fee is non-refundable. After
                  that, your payment is non-refundable.
                </>
              ) : (
                <>
                  If cancelled after that, a cancellation fee of{" "}
                  <strong style={{ color: "#111008" }}>
                    ${policy.cancellation_fee}
                  </strong>{" "}
                  will be charged to your card on file.
                </>
              )}
            </Typography>
          </>
        )}
      </Box>

      {/* Location card */}
      {(tenant?.address_line1 || tenant?.city) && (
        <Box
          component={Paper}
          elevation={1}
          sx={{
            width: "100%",
            maxWidth: "620px",
            borderRadius: 2,
            border: "0.1px solid",
            borderColor: "customDivider",
            backgroundColor: "offWhite",
            mt: 2,
            overflow: "hidden",
          }}
        >
          {(() => {
            const addressParts = [
              tenant?.address_line1,
              tenant?.address_line2,
              tenant?.city,
              tenant?.state,
              tenant?.zip,
            ].filter(Boolean);
            const addressQuery = encodeURIComponent(addressParts.join(", "));
            return (
              <Box sx={{ width: "100%", height: 200 }}>
                <iframe
                  title="Shop Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps?q=${addressQuery}&output=embed`}
                />
              </Box>
            );
          })()}

          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {tenant?.name}
            </Typography>
            {tenant?.address_line1 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {tenant.address_line1}
                {tenant.address_line2 ? `, ${tenant.address_line2}` : ""}
              </Typography>
            )}
            {(tenant?.city || tenant?.state || tenant?.zip) && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {[tenant?.city, tenant?.state, tenant?.zip]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
            {tenant?.phone && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {formatPhoneNumber(tenant.phone)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 4,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button variant="contained" color="primary" onClick={onBookAgain}>
          Book Another Appointment
        </Button>
      </Box>
    </Box>
  );
};

export default SuccessfullBooking;
